import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { galleryAPI } from '../../services/api';
import { GalleryImage } from '../../types';
import './GalleryManagement.css';

const GalleryManagement: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState<GalleryImage | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_active: true,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAllAdmin();
      setImages(response.data.images);
    } catch (error) {
      console.error('Galeri yükleme hatası:', error);
      setError('Resimler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (image?: GalleryImage) => {
    if (image) {
      setEditMode(true);
      setCurrentImage(image);
      setFormData({
        title: image.title,
        description: image.description || '',
        is_active: image.is_active,
      });
      setPreviewUrl(`http://localhost:5000/${image.image_path}`);
    } else {
      setEditMode(false);
      setCurrentImage(null);
      setFormData({
        title: '',
        description: '',
        is_active: true,
      });
      setPreviewUrl('');
      setSelectedFile(null);
    }
    setShowModal(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      title: '',
      description: '',
      is_active: true,
    });
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
    setUploadProgress(0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        setError('Lütfen bir resim dosyası seçin');
        return;
      }

      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Preview oluştur
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Başlık gereklidir');
      return;
    }

    try {
      if (editMode && currentImage) {
        // Güncelleme
        await galleryAPI.update(currentImage.id, formData);
        setSuccess('Resim başarıyla güncellendi');
      } else {
        // Yeni resim yükleme
        if (!selectedFile) {
          setError('Lütfen bir resim seçin');
          return;
        }

        const uploadFormData = new FormData();
        uploadFormData.append('image', selectedFile);
        uploadFormData.append('title', formData.title);
        if (formData.description) {
          uploadFormData.append('description', formData.description);
        }
        uploadFormData.append('is_active', formData.is_active.toString());

        setUploadProgress(50);
        await galleryAPI.upload(uploadFormData);
        setUploadProgress(100);
        setSuccess('Resim başarıyla yüklendi');
      }

      setTimeout(() => {
        handleCloseModal();
        fetchImages();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'İşlem başarısız');
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu resmi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await galleryAPI.delete(id);
      setSuccess('Resim başarıyla silindi');
      fetchImages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Silme işlemi başarısız');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleActive = async (image: GalleryImage) => {
    try {
      await galleryAPI.update(image.id, {
        is_active: !image.is_active,
      });
      fetchImages();
    } catch (error) {
      console.error('Durum değiştirme hatası:', error);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>
            <i className="bi bi-shield-lock-fill me-2"></i>
            Admin Panel
          </h2>
        </div>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item">
            <i className="bi bi-speedometer2"></i>
            Dashboard
          </Link>
          <Link to="/admin/announcements" className="nav-item">
            <i className="bi bi-megaphone"></i>
            Duyurular
          </Link>
          <Link to="/admin/gallery" className="nav-item active">
            <i className="bi bi-images"></i>
            Galeri
          </Link>
          <Link to="/admin/faqs" className="nav-item">
            <i className="bi bi-question-circle"></i>
            SSS
          </Link>
          <Link to="/admin/users" className="nav-item">
            <i className="bi bi-people"></i>
            Kullanıcılar
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="btn btn-secondary w-100 mb-2">
            <i className="bi bi-house-door me-2"></i>
            Ana Sayfa
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>Galeri Yönetimi</h1>
            <p className="text-muted">Resimleri yönetin</p>
          </div>
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            <i className="bi bi-cloud-upload me-2"></i>
            Resim Yükle
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {/* Gallery Grid */}
        <div className="gallery-container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-images" style={{ fontSize: '3rem' }}></i>
              <p className="mt-3">Henüz resim yok</p>
            </div>
          ) : (
            <div className="gallery-grid">
              {images.map((image) => (
                <div key={image.id} className="gallery-item">
                  <div className="gallery-image-wrapper">
                    <img
                      src={`http://localhost:5000/${image.image_path}`}
                      alt={image.title}
                      className="gallery-image"
                    />
                    <div className="gallery-overlay">
                      <button
                        onClick={() => handleOpenModal(image)}
                        className="btn-icon btn-icon-primary"
                        title="Düzenle"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(image.id)}
                        className="btn-icon btn-icon-danger"
                        title="Sil"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div className="gallery-info">
                    <h3 className="gallery-title">{image.title}</h3>
                    {image.description && (
                      <p className="gallery-description">{image.description}</p>
                    )}
                    <div className="gallery-meta">
                      <button
                        onClick={() => handleToggleActive(image)}
                        className={`badge ${
                          image.is_active ? 'badge-success' : 'badge-secondary'
                        }`}
                      >
                        {image.is_active ? 'Aktif' : 'Pasif'}
                      </button>
                      <span className="gallery-date">
                        {new Date(image.created_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container modal-gallery" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Resim Bilgilerini Düzenle' : 'Yeni Resim Yükle'}</h3>
              <button onClick={handleCloseModal} className="modal-close" title="Kapat" aria-label="Kapat">
                <i className="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                </div>
              )}

              {/* Image Upload */}
              {!editMode && (
                <div className="form-group">
                  <label htmlFor="image">Resim Seç *</label>
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="image"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                    <label htmlFor="image" className="file-label">
                      <i className="bi bi-cloud-upload me-2"></i>
                      {selectedFile ? selectedFile.name : 'Resim Seç (Max 5MB)'}
                    </label>
                  </div>
                </div>
              )}

              {/* Image Preview */}
              {previewUrl && (
                <div className="image-preview">
                  <img src={previewUrl} alt="Preview" />
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                    {uploadProgress}%
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="title">Başlık *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Resim başlığı"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Açıklama</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Resim açıklaması (opsiyonel)"
                />
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  className="form-check-input"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <label htmlFor="is_active" className="form-check-label">
                  Aktif
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploadProgress > 0 && uploadProgress < 100}>
                  <i className="bi bi-save me-2"></i>
                  {editMode ? 'Güncelle' : 'Yükle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManagement;
